import { useState, useEffect, useContext, useRef } from "react";
import {
  Grid,
  Typography,
  Checkbox,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  Divider,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { EventContext } from "../../../contexts/EventContext";
import EditProfileDialog from "../../../components/EditProfileDialog";
import SwapDialog from "./SwapDialog";
import TransferDialog from "./TransferDialog";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import request from "../../../utilities/requests";
import { toast } from "../../../utilities/SnackBarUtils";

const AttendeeList = () => {
  const {
    userData,
    pairingEvent,
    attendees,
    setAttendees,
    pagination,
    setPagination,
    percentages,
    setPercentages,
  } = useContext(EventContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [presentLabel, setPresentLabel] = useState("Mark Present");
  const [anchor, setAnchor] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUserOpen, setEditUserOpen] = useState(false);
  // const [confirmOpen, setConfirmOpen] = useState(false);
  const [removeUserOpen, setRemoveUserOpen] = useState(false);
  const [swapOpen, setSwapOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [ticketingEnabled, setTicketingEnabled] = useState(false);
  const [hasWaiver, setHasWaiver] = useState(false);
  const [canUpdateVip, setCanUpdateVip] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [checkingInIds, setCheckingInIds] = useState(new Set());
  const [hasMore, setHasMore] = useState(true);
  const loadMoreRef = useRef(null);
  const openSearch = () => {
    setSearchOpen(true);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery("");
    setSearching(false);
    setSearchResults([]);
  };
  const runSearch = async () => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      toast.error("Please enter at least 2 characters");
      return;
    }

    const result = await request(
      `pairingevent/search?pairingEventId=${
        pairingEvent._id
      }&q=${encodeURIComponent(searchQuery)}`,
    );

    if (result.success) {
      setSearchResults(result.attendees);
      setSearching(true);
    } else {
      toast.error(result.error || "Search failed");
    }
  };
  const handleSearchSelect = (attendee) => {
    setSelectedUser(attendee);
    setEditUserOpen(true);
    setSearchOpen(false);
    setSearchQuery("");
    setSearchResults([]);
    setSearching(false);
  };

  useEffect(() => {
    if (userData?.org?.config?.ticketingEnabled) {
      setTicketingEnabled(true);
    }
    if (
      userData?.role === "superadmin" ||
      userData?.permissions?.canUpdateVip
    ) {
      setCanUpdateVip(true);
    }
  }, [userData]);

  useEffect(() => {
    if (pairingEvent && pairingEvent?.event?.waiver) {
      setHasWaiver(true);
    }
  }, [pairingEvent]);

  const handleCheck = async (i) => {
    const attendee = attendees[i];
    if (!attendee || checkingInIds.has(attendee._id)) return;

    setCheckingInIds((prev) => new Set(prev).add(attendee._id));

    const nextValue = !attendee.checkedIn;
    const result = await updateCheckinStatus(attendee._id, nextValue);

    if (result) {
      setAttendees((prev) =>
        prev.map((a, index) =>
          index === i ? { ...a, checkedIn: nextValue } : a,
        ),
      );
    }

    setCheckingInIds((prev) => {
      const updated = new Set(prev);
      updated.delete(attendee._id);
      return updated;
    });
  };

  const updateCheckinStatus = async (userId, checkedIn) => {
    const body = {
      eventId: pairingEvent.event._id,
      userId,
      checkedIn,
    };
    const result = await request("events/updatecheckin", "post", body);
    if (!result.success) {
      let error;
      if (result?.incompleteProfile) {
        error = "Cannot check in. Attendee profile is not complete.";
      } else if (result?.nameRequired) {
        error = "Cannot check in. Attendee needs a first and last name."
      } else if (result?.photoRequired) {
        error = "Cannot check in. This event requires a profile photo - attendee does not have one."
      } else {
        error = `Unknown error: ${result.error}`
      }
      toast.error(error);
      return false;
    } else {
      return true;
    }
  };

  const handleMenuOpen = (event, item) => {
    setAnchor(event.currentTarget);
    setMenuOpen(true);
    setSelectedUser(item);
    if (item?.isPresent) {
      setPresentLabel("Mark Not Present");
    } else {
      setPresentLabel("Mark Present");
    }
  };

  const getAttendees = async (offset = 0, append = false) => {
    if (loadingMore) return;

    setLoadingMore(true);

    const result = await request(
      `pairingevent/attendees?pairingEventId=${pairingEvent._id}&limit=100&offset=${offset}`,
    );

    if (result.success) {
      if (append) {
        setAttendees((prev) => [...prev, ...result.attendees]);
      } else {
        setAttendees(result.attendees);
      }

      // setPercentages(result.percentages);
      setPagination(result.pagination);

      if (result.attendees.length < 100) {
        setHasMore(false);
      }
    }

    setLoadingMore(false);
  };

  useEffect(() => {
    if (pairingEvent?._id) {
      setHasMore(true);
      setSearching(false);
      // getAttendees(0, false);
    }
  }, [pairingEvent]);

  useEffect(() => {
    if (searching) return;
    if (!loadMoreRef.current || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          const nextOffset =
            (pagination?.offset || 0) + (pagination?.limit || 100);
          getAttendees(nextOffset, true);
        }
      },
      { threshold: 1.0 },
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [loadMoreRef.current, hasMore, loadingMore, pagination, searching]);

  const handleDialogClose = () => {
    getAttendees();
    setEditUserOpen(false);
    setRemoveUserOpen(false);
    setSelectedUser(null);
    setMenuOpen(false);
  };

  const handleEdit = () => {
    setEditUserOpen(true);
    setMenuOpen(false);
  };

  const handleSwap = () => {
    setSwapOpen(true);
  };

  const handleSwapClose = () => {
    getAttendees();
    setSwapOpen(false);
    setSelectedUser(null);
    setMenuOpen(false);
  };

  const handleTransfer = () => {
    setTransferOpen(true);
    setMenuOpen(false);
  };

  const handleRemove = () => {
    setRemoveUserOpen(true);
    setMenuOpen(false);
  };

  const handleConfirm = async () => {
    const body = {
      userId: selectedUser._id,
      eventId: pairingEvent.event._id,
    };
    const result = await request("events/removeattendee", "post", body);
    if (result.success) {
      toast.success("User removed from event");
      handleDialogClose();
    }
  };

  const markPresent = async () => {
    const body = {
      userId: selectedUser._id,
      eventId: pairingEvent?.event?._id,
    };
    let delta = 0;
    if (selectedUser?.isPresent === true) {
      delta = -1;
    } else if (selectedUser?.isPresent === false) {
      delta = 1;
    }

    const result = await request(`events/markpresent`, "post", body);
    if (result.success) {
      setAttendees((prev) =>
        prev.map((attendee) =>
          attendee._id === selectedUser._id
            ? { ...attendee, isPresent: result.isPresent }
            : attendee,
        ),
      );
      setPercentages((prev) => ({
        ...prev,
        isPresent: {
          ...prev.isPresent,
          total: (prev.isPresent?.total || 0) + delta,
        },
      }));
      if (result?.isPresent === true) {
        toast.success("Attendee marked present");
      } else if (result?.isPresent === false) {
        toast.success("Attendee marked not present");
      }
    }
    setMenuOpen(false);
  };

  const updateNoShow = async () => {
    const body = {
      userId: selectedUser._id,
      eventId: pairingEvent?.event?._id,
    };
    const result = await request("events/updatenoshow", "post", body);
    if (result.success) {
      setAttendees((prev) =>
        prev.map((attendee) =>
          attendee._id === selectedUser._id
            ? { ...attendee, noShow: result.noShow }
            : attendee,
        ),
      );
      if (result?.noShow === true) {
        toast.success("Attendee as not attending");
      } else if (result?.isPresent === false) {
        toast.success("Attendee marked as attending");
      }
    }
    setMenuOpen(false);
  };

  const updateVip = async () => {
    const body = {
      userId: selectedUser._id,
      eventId: pairingEvent?.event?._id,
    };
    const result = await request("events/updatevip", "post", body);
    if (result.success) {
      setAttendees((prev) =>
        prev.map((attendee) =>
          attendee._id === selectedUser._id
            ? { ...attendee, vip: result.vip }
            : attendee,
        ),
      );
      if (result?.vip === true) {
        toast.success("Attendee marked as VIP");
      } else if (result?.vip === false) {
        toast.success("VIP status removed");
      }
    }
    setMenuOpen(false);
  };

  const renderAttendees = () => {
    if (attendees.length > 0) {
      return (
        <Grid container direction="column">
          <Grid item style={{ marginBottom: -20 }}>
            <Grid container direction="row" alignItems="center">
              <Grid item xs={6}>
                <Typography variant="button">name</Typography>
              </Grid>
              <Grid
                item
                xs={4}
                style={{ display: "flex", justifyContent: "center" }}
              >
                <Typography variant="button" align="center">
                  checked in
                </Typography>
              </Grid>
              <Grid
                item
                xs={1}
                style={{ display: "flex", justifyContent: "center" }}
              ></Grid>
            </Grid>
          </Grid>
          {attendees.map((item, i) => {
            return (
              <Grid item key={item._id}>
                <Grid container direction="row" alignItems="center" style={{ minHeight: 48 }}>
                  <Grid
                    item
                    xs={6}
                    style={{ display: "flex", flexDirection: "row" }}
                  >
                    {item?.vip && (
                      <Tooltip title="Attendee is a VIP">
                        <WorkspacePremiumIcon />
                      </Tooltip>
                    )}
                    {item?.incompleteProfile && (
                      <Tooltip title="Attendee's profile is incomplete. In order to check into the event, they must complete their profile">
                        <PriorityHighIcon />
                      </Tooltip>
                    )}
                    {item?.noShow && (
                      <Tooltip title="Attendee indicated that they are unable to attend">
                        <EventBusyIcon />
                      </Tooltip>
                    )}
                    {hasWaiver && item?.signedWaiver === false ? (
                      <Tooltip title="Attendee has not yet signed the waiver required for this event.">
                        <EditIcon style={{ width: 20, marginRight: 10 }} />
                      </Tooltip>
                    ) : null}
                    {pairingEvent?.event?.trackIsPresent && item?.isPresent && (
                      <Tooltip title="Attendee's ticket has been scanned">
                        <CheckIcon sx={{ color: "green" }} />
                      </Tooltip>
                    )}
                    <Typography>
                      {item.profile?.firstName} {item.profile?.lastName}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={4}
                    style={{ display: "flex", justifyContent: "center" }}
                  >
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {checkingInIds.has(item._id) ? (
                        <CircularProgress size={20} />
                      ) : (
                        <Checkbox
                          checked={item.checkedIn}
                          onChange={() => handleCheck(i)}
                          style={{ padding: 0 }}
                        />
                      )}
                    </div>
                  </Grid>
                  <Grid
                    item
                    xs={1}
                    style={{ display: "flex", justifyContent: "center" }}
                  >
                    <MoreVertIcon
                      onClick={(event) => handleMenuOpen(event, item)}
                      style={{ cursor: "pointer" }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
          <div ref={loadMoreRef} style={{ height: 20 }} />
          {loadingMore && (
            <Grid container justifyContent="center" style={{ padding: 16 }}>
              <CircularProgress size={24} />
            </Grid>
          )}
          <Menu
            anchorEl={anchor}
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
          >
            {pairingEvent?.event?.trackIsPresent && (
              <>
                <MenuItem onClick={markPresent}>{presentLabel}</MenuItem>
                <Divider />
              </>
            )}
            <MenuItem onClick={handleEdit}>Edit</MenuItem>
            <MenuItem onClick={handleSwap}>Swap</MenuItem>
            {ticketingEnabled && (
              <MenuItem onClick={handleTransfer}>Transfer</MenuItem>
            )}
            <MenuItem onClick={handleRemove}>Remove Attendee</MenuItem>
            <Divider />
            <MenuItem onClick={updateNoShow}>
              {selectedUser?.noShow ? "Cancel No-Show" : "Mark No-Show"}
            </MenuItem>
            {canUpdateVip && (
              <MenuItem onClick={updateVip}>
                {selectedUser?.vip ? "Remove VIP" : "Make VIP"}
              </MenuItem>
            )}
          </Menu>
          <EditProfileDialog
            open={editUserOpen}
            close={handleDialogClose}
            user={selectedUser}
            eventId={pairingEvent?.event?._id}
          />
        </Grid>
      );
    } else {
      return (
        <Typography>
          No attendees in database. Try syncing with Eventbrite
        </Typography>
      );
    }
  };

  return (
    <>
      <Grid container direction="column" spacing={3}>
        <Grid
          item
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {pairingEvent?.event?.trackIsPresent && (
            <Typography variant="subtitle2">
              Present:{" "}
              {percentages?.isPresent?.total
                ? percentages.isPresent?.total
                : ""}
            </Typography>
          )}
          <Typography variant="subtitle2">
            Checked In:{" "}
            {percentages?.checkedIn?.total ? percentages.checkedIn.total : ""}
          </Typography>
          <Typography variant="subtitle2">
            Boys:{" "}
            {percentages?.checkedIn?.boys ? percentages.checkedIn?.boys : ""}
          </Typography>
          <Typography variant="subtitle2">
            Girls:{" "}
            {percentages?.checkedIn?.girls ? percentages?.checkedIn?.girls : ""}
          </Typography>
          <IconButton onClick={openSearch} size="small">
            <SearchIcon />
          </IconButton>
        </Grid>
        <Grid item>{renderAttendees()}</Grid>
        <Dialog open={searchOpen} onClose={closeSearch} fullWidth maxWidth="sm">
          <DialogTitle>Search Attendees</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label="Search by name or email"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  runSearch();
                }
              }}
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={runSearch} size="small">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Typography variant="caption">
              Start typing to find a specific attendee
            </Typography>
            {searchResults.length > 0 && (
              <Grid
                container
                direction="column"
                spacing={1}
                style={{ marginTop: 16 }}
              >
                <Grid item>
                  <Grid container alignItems="center">
                    <Grid item />
                    <Grid item xs>
                      <Typography variant="caption" color="textSecondary">
                        Attendee
                      </Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography variant="caption" color="textSecondary">
                        Checked In
                      </Typography>
                    </Grid>
                    <Grid item xs={1}></Grid>
                  </Grid>
                </Grid>
                {searchResults.map((item) => (
                  <Grid
                    item
                    key={item._id}
                    style={{ cursor: "pointer", padding: 8, borderRadius: 4 }}
                  >
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item>
                        {item?.noShow && (
                          <Tooltip title="Attendee indicated that they are unable to attend">
                            <EventBusyIcon />
                          </Tooltip>
                        )}
                        {pairingEvent?.event?.trackIsPresent &&
                          item?.isPresent && (
                            <Tooltip title="Attendee's ticket has been scanned">
                              <CheckIcon sx={{ color: "green" }} />
                            </Tooltip>
                          )}
                        {item?.vip && (
                          <Tooltip title="Attendee is a VIP">
                            <WorkspacePremiumIcon />
                          </Tooltip>
                        )}
                      </Grid>
                      <Grid item xs onClick={() => handleSearchSelect(item)}>
                        <Typography variant="body1">
                          {item.profile?.firstName} {item.profile?.lastName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {item.email}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <div
                          style={{
                            width: 24,
                            height: 24,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {checkingInIds.has(item._id) ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Checkbox
                              checked={item.checkedIn}
                              onClick={(e) => e.stopPropagation()}
                              onChange={async () => {
                                if (checkingInIds.has(item._id)) return;

                                setCheckingInIds((prev) => new Set(prev).add(item._id));

                                const result = await updateCheckinStatus(
                                  item._id,
                                  !item.checkedIn,
                                );

                                if (result) {
                                  setSearchResults((prev) =>
                                    prev.map((a) =>
                                      a._id === item._id
                                        ? { ...a, checkedIn: !a.checkedIn }
                                        : a,
                                    ),
                                  );
                                  setAttendees((prev) =>
                                    prev.map((a) =>
                                      a._id === item._id
                                        ? { ...a, checkedIn: !a.checkedIn }
                                        : a,
                                    ),
                                  );
                                }

                                setCheckingInIds((prev) => {
                                  const updated = new Set(prev);
                                  updated.delete(item._id);
                                  return updated;
                                });
                              }}
                              style={{ padding: 0 }}
                            />
                          )}
                        </div>
                      </Grid>
                      <Grid item>
                        <MoreVertIcon
                          onClick={(event) => handleMenuOpen(event, item)}
                          style={{ cursor: "pointer" }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeSearch}>Close</Button>
          </DialogActions>
        </Dialog>
        <Dialog open={removeUserOpen} onClose={() => setRemoveUserOpen(false)}>
          <DialogTitle>Please confirm</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to remove{" "}
              <strong>
                {selectedUser?.profile?.firstName}{" "}
                {selectedUser?.profile?.lastName}
              </strong>{" "}
              from the event?
            </Typography>
            {ticketingEnabled ? (
              <Typography style={{ paddingTop: 20 }}>
                Removing an attendee here will <strong>not</strong> issue a
                refund. If the ticket was purchased through this system, please
                refund via Users → Search Attendee. From there, you can also
                remove them from the event. Note: if you remove them here,
                ticket availability will update on this system and any linked
                platforms. (similar to refunds)
              </Typography>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRemoveUserOpen(false)}>Cancel</Button>
            <Button onClick={handleConfirm}>Confirm</Button>
          </DialogActions>
        </Dialog>
        <SwapDialog
          open={swapOpen}
          close={handleSwapClose}
          user={selectedUser}
        />
        <TransferDialog
          open={transferOpen}
          close={() => {
            setTransferOpen(false);
            getAttendees();
          }}
          user={selectedUser}
        />
      </Grid>
    </>
  );
};

export default AttendeeList;
